'use client'

import { useEffect, useRef } from 'react'

import { useViewport } from '@/components/findex-connect/hooks/useViewport'
import { useThemeStore } from '@/components/findex-connect/store/ThemeStore'
import { FeatureSb, GetStartedBlockSb } from '@/components/findex-connect/types/component-types-sb'

import { BlurryLightsDaytime } from '../BlurryLights/BlurryLightsDaytime'
import { TickIcon } from '../Icons/TickIcon/TickIcon'

type GetStartedBlockProps = {
  data: GetStartedBlockSb
}

export const GetStartedBlock: React.FC<GetStartedBlockProps> = ({ data }) => {
  const { viewportWidth } = useViewport()
  const sectionRef = useRef<HTMLDivElement>(null)
  const activeSection = useThemeStore((state) => state.activeSection)
  const isMobileFormVisible = useThemeStore((state) => state.isMobileFormVisible)

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-10% 0%',
      threshold: 0.2,
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          useThemeStore.setState({ activeSection: 'get-started' })
        }
      })
    }, observerOptions)

    if (sectionRef?.current) {
      observer.observe(sectionRef.current)
    }
  }, [sectionRef, activeSection])

  // Data
  if (!data) return null
  const { heading, mainText, ctaText, image, features } = data

  const renderFeatureText = (feature: FeatureSb) => {
    const number = Number(feature.numberOfBoldWords)
    const words = feature.text.split(' ')
    const boldWords = words.slice(0, number)
    const remainingWords = words.slice(number)
    const boldText = boldWords.join(' ')
    const remainingText = remainingWords.join(' ')
    return (
      <span>
        <strong>{boldText} </strong> {remainingText}
      </span>
    )
  }

  return (
    <div
      className="GETSTARTED-BLOCK relative flex w-full flex-col overflow-hidden bg-gradient-to-b from-white via-black/0 to-white/0 md:min-h-[800px]"
      ref={sectionRef}
    >
      <BlurryLightsDaytime />
      <div className="GETSTARTED-OUTER max-w-container md:p-gutter relative z-10 mx-auto my-0 flex w-full flex-col md:my-52">
        <div className="GETSTARTED-CARD flex grow-1 flex-col-reverse overflow-hidden border-black/20 bg-white pb-10 md:rounded-2xl md:border md:pb-0 lg:flex-row">
          <div className="GETSTARTED-CARD-CONTENT flex flex-col gap-1 p-6 md:mr-[10%] md:gap-3 md:p-14 lg:mr-0">
            <h1 className="HEADING text-prominent mb-6 self-start" tabIndex={0}>
              {heading}
            </h1>
            <p className="MAINTEXT para-lg text-text">{mainText}</p>
            <ul className="FEATURE-LIST flex flex-col gap-y-4 pt-8 md:pt-10">
              {features?.map((feature) => (
                <li className="textblock-md text-prominent flex gap-4" key={feature.text}>
                  <TickIcon /> {renderFeatureText(feature)}
                </li>
              ))}
            </ul>
            <button
              tabIndex={0}
              onClick={() => {
                if (viewportWidth > 1024) {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth',
                  })
                } else {
                  if (!isMobileFormVisible) {
                    useThemeStore.setState({ isMobileFormVisible: true })
                  }
                }
              }}
              className="bg-primary-dark tab-outline-primary hover:bg-primary-hover mt-8 cursor-pointer self-start rounded-full px-5 py-3 text-lg font-semibold text-white transition-all duration-150 ease-in-out max-sm:self-stretch"
            >
              {ctaText}
            </button>
          </div>
          <div className="IMAGE-CONTAINER h-[260px] w-full shrink-0 bg-black/10 md:h-[380px] lg:!h-auto lg:w-2/5">
            {image?.filename && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={image?.filename}
                alt={image?.alt || 'Get started image'}
                width={'100%'}
                height={'100%'}
                className="h-full w-full object-cover object-[0%_20%] lg:object-center"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
